import { AxiosError } from 'axios'

interface ErrorResponse {
  message?: string
  error?: string
  errors?: unknown
  statusCode?: number
}

/**
 * Extracts a readable error message from an Axios error response
 * Handles 500 errors with a generic message and shows backend errors for other codes
 */
export function getErrorMessage(error: unknown): string {
  // If it's an AxiosError
  if (error && typeof error === 'object' && 'isAxiosError' in error) {
    const axiosError = error as AxiosError<ErrorResponse>

    // Handle 500 errors (server errors)
    if (axiosError.response?.status && axiosError.response.status >= 500) {
      return 'Error del servidor. Por favor, intente nuevamente en unos momentos.'
    }

    // Extract backend message if available
    if (axiosError.response?.data) {
      const errorData = axiosError.response.data

      if (typeof errorData === 'string') {
        return errorData
      }

      if (typeof errorData === 'object' && errorData !== null) {
        // Try different properties where the message might be
        if (errorData.message) {
          // If it's an array of messages
          if (Array.isArray(errorData.message)) {
            return errorData.message.join(', ')
          }
          return errorData.message
        }

        if (errorData.error) {
          return errorData.error
        }

        if (errorData.errors) {
          if (typeof errorData.errors === 'string') {
            return errorData.errors
          }
          return JSON.stringify(errorData.errors)
        }
      }
    }

    // If there's no response (network error)
    if (!axiosError.response) {
      return 'Error de conexión. Verifique su conexión a internet e intente nuevamente.'
    }

    // If there's a message in the error
    if (axiosError.message) {
      return axiosError.message
    }
  }

  // If it's a generic error with response (without isAxiosError)
  if (
    error &&
    typeof error === 'object' &&
    'response' in error &&
    error.response &&
    typeof error.response === 'object'
  ) {
    const response = error.response as {
      status?: number
      data?: ErrorResponse
    }

    // Handle 500 errors
    if (response.status && response.status >= 500) {
      return 'Error del servidor. Por favor, intente nuevamente en unos momentos.'
    }

    // Extract backend message
    if (response.data) {
      const errorData = response.data

      if (typeof errorData === 'string') {
        return errorData
      }

      if (typeof errorData === 'object' && errorData !== null) {
        if (errorData.message) {
          if (Array.isArray(errorData.message)) {
            return errorData.message.join(', ')
          }
          return errorData.message
        }

        if (errorData.error) {
          return errorData.error
        }

        if (errorData.errors) {
          if (typeof errorData.errors === 'string') {
            return errorData.errors
          }
          return JSON.stringify(errorData.errors)
        }
      }
    }
  }

  // If it's a native JavaScript Error
  if (error instanceof Error) {
    return error.message
  }

  // If it's a string
  if (typeof error === 'string') {
    return error
  }

  // Generic fallback
  return 'Ha ocurrido un error inesperado'
}

/**
 * Determines if an error is a network/connection error
 */
export function isNetworkError(error: unknown): boolean {
  if (error && typeof error === 'object') {
    const err = error as {
      response?: { status?: number }
      code?: string
      name?: string
      message?: string
    }

    return (
      err.response?.status === 0 ||
      err.code === 'NETWORK_ERROR' ||
      err.code === 'ERR_NETWORK' ||
      err.name === 'NetworkError' ||
      err.message?.includes('Network Error') ||
      err.message?.includes('fetch') ||
      err.message?.includes('Failed to fetch') ||
      err.message?.includes('ERR_INTERNET_DISCONNECTED') ||
      !navigator.onLine
    )
  }

  return false
}

/**
 * Determines if an error is a server error (5xx)
 */
export function isServerError(error: unknown): boolean {
  if (error && typeof error === 'object' && 'response' in error) {
    const err = error as { response?: { status?: number } }
    return Boolean(err.response?.status && err.response.status >= 500)
  }
  return false
}

/**
 * Determines if an error is a client error (4xx)
 */
export function isClientError(error: unknown): boolean {
  if (error && typeof error === 'object' && 'response' in error) {
    const err = error as { response?: { status?: number } }
    return Boolean(
      err.response?.status &&
        err.response.status >= 400 &&
        err.response.status < 500
    )
  }
  return false
}
